import React, { useState, useEffect, useRef } from "react";
import { Send, Calendar, Clock } from "lucide-react";

const API_URL = "https://task-scheduler-ai.akosuaaidoo83.workers.dev";

const TaskSchedulerAI = () => {
  const [messages, setMessages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [userId] = useState("user_" + Math.random().toString(36).substr(2, 9));
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  useEffect(() => {
    loadState();
  }, []);

  const loadState = async () => {
    try {
      const response = await fetch(`${API_URL}/api/state?userId=${userId}`);
      const data = await response.json();

      if (data.conversationHistory) {
        setMessages(data.conversationHistory);
      }
      if (data.tasks) {
        setTasks(data.tasks);
      }
    } catch (error) {
      console.error("Error loading state:", error);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: input,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          message: input,
        }),
      });

      const data = await response.json();

      const assistantMessage = {
        role: "assistant",
        content: data.response,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setTasks(data.tasks || []);
    } catch (error) {
      console.error("Error:", error);
      const errorMessage = {
        role: "assistant",
        content:
          "Sorry, I'm having trouble connecting. Make sure your Worker is running!",
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800 border-red-200";
      case "medium":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "low":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const handleCompleteTask = async (taskId) => {
    console.log("Completing task:", taskId);
    try {
      const response = await fetch(`${API_URL}/api/tasks/complete`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ taskId, userId }),
      });

      const data = await response.json();
      console.log("Response:", data);
      if (data.success) {
        console.log("Updated tasks:", data.tasks);
        setTasks(data.tasks);
      }
    } catch (error) {
      console.error("Error completing task:", error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const response = await fetch(`${API_URL}/api/tasks/delete`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ taskId, userId }),
      });

      const data = await response.json();
      if (data.success) {
        setTasks(data.tasks);
      }
    } catch (error) {
      console.error("Error deleting task:", error);
    }
  };

  const handleClearCompleted = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tasks/clear-completed`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
      const data = await response.json();
      if (data.success) {
        setTasks(data.tasks);
        console.log(`Deleted ${data.deletedCount} completed tasks`);
      }
    } catch (error) {
      console.error("Error clearing completed tasks:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Task Scheduler AI
          </h1>
          <p className="text-gray-600">
            Tell me what you need to do, I'll organize it for you
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div
            className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col"
            style={{ height: "600px" }}
          >
            <div className="bg-indigo-600 text-white p-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Send size={20} />
                Chat
              </h2>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && (
                <div className="text-center text-gray-500 mt-8">
                  <p className="mb-2">
                    👋 Hi! I'm your task scheduling assistant.
                  </p>
                  <p className="text-sm">
                    Try: "I need to write a report and go grocery shopping
                    today"
                  </p>
                </div>
              )}

              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      msg.role === "user"
                        ? "bg-indigo-600 text-white"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 text-gray-800 px-4 py-2 rounded-lg">
                    <div className="flex gap-2">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.1s" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="border-t p-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                  placeholder="Type your tasks here..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={loading}
                />
                <button
                  onClick={sendMessage}
                  disabled={loading || !input.trim()}
                  className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  <Send size={20} />
                </button>
              </div>
            </div>
          </div>

          <div
            className="bg-white rounded-lg shadow-lg overflow-hidden"
            style={{ height: "600px" }}
          >
            <div className="bg-indigo-600 text-white p-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Calendar size={20} />
                Your Tasks ({tasks.filter((t) => t.status === "pending").length}
                )
              </h2>
              {tasks.filter((t) => t.status === "completed").length > 0 && (
                <button
                  onClick={handleClearCompleted}
                  className="bg-white text-indigo-600 px-3 py-1 rounded text-sm font-medium hover:bg-indigo-50 transition-colors"
                >
                  Clear Completed (
                  {tasks.filter((t) => t.status === "completed").length})
                </button>
              )}
            </div>
            <div
              className="p-4 overflow-y-auto"
              style={{ height: "calc(600px - 64px)" }}
            >
              {tasks.length === 0 ? (
                <div className="text-center text-gray-500 mt-8">
                  <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>No tasks yet. Start chatting to add some!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* PENDING TASKS */}
                  {tasks
                    .filter((t) => t.status === "pending")
                    .sort(
                      (a, b) =>
                        new Date(a.scheduledStart) - new Date(b.scheduledStart),
                    )
                    .map((task) => (
                      <div
                        key={task.id}
                        className={`border-2 rounded-lg p-4 ${getPriorityColor(task.priority)}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-lg">
                            {task.title}
                          </h3>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium px-2 py-1 rounded bg-white bg-opacity-50">
                              {task.priority}
                            </span>
                            <button
                              onClick={() => handleCompleteTask(task.id)}
                              className="bg-green-500 text-white w-7 h-7 rounded hover:bg-green-600 transition-colors flex items-center justify-center"
                              title="Mark as complete"
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => handleDeleteTask(task.id)}
                              className="bg-red-500 text-white w-7 h-7 rounded hover:bg-red-600 transition-colors flex items-center justify-center text-lg"
                              title="Delete task"
                            >
                              ×
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1">
                            <Clock size={14} />
                            <span>{formatTime(task.scheduledStart)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span>Duration: {task.duration} min</span>
                          </div>
                        </div>
                      </div>
                    ))}

                  {tasks.filter((t) => t.status === "completed").length > 0 && (
                    <div className="mt-6 pt-4 border-t-2 border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-500 mb-3">
                        ✓ Completed Tasks
                      </h3>
                      {tasks
                        .filter((t) => t.status === "completed")
                        .map((task) => (
                          <div
                            key={task.id}
                            className="border-2 rounded-lg p-4 bg-gray-50 border-gray-200 opacity-60 mb-2"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <h3 className="font-semibold text-lg line-through text-gray-500">
                                {task.title}
                              </h3>
                              <button
                                onClick={() => handleDeleteTask(task.id)}
                                className="bg-red-400 text-white w-7 h-7 rounded hover:bg-red-500 transition-colors flex items-center justify-center text-lg"
                                title="Delete task"
                              >
                                ×
                              </button>
                            </div>

                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <div className="flex items-center gap-1">
                                <Clock size={14} />
                                <span className="line-through">
                                  {formatTime(task.scheduledStart)}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="line-through">
                                  Duration: {task.duration} min
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold text-gray-800 mb-2">💡 Try these:</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600">
            <div>"I need to write a report and call my mom"</div>
            <div>"Add workout and grocery shopping to my day"</div>
            <div>"Schedule 2 hours for coding practice"</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskSchedulerAI;
